import { Grid2, Skeleton } from '@mui/material';
import { isShibUser } from '@thro-helpers/users';
import { StudentJobOffer } from '@thro-types/jobmarket';

import { useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Waypoint } from 'react-waypoint';

import { useJobTypesQuery } from 'api/hooks/jobmarket/jobtypes';
import { useStudentJobOfferingsQuery } from 'api/hooks/jobmarket/studentOffers';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { CreateButton, RichList } from 'app/components';
import { Filter } from 'app/components/Filters';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { sortCourses } from 'utils/courses';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useContentScrollPosition } from 'utils/hooks/useContentScrollPosition';
import usePageHeading from 'utils/hooks/usePageHeading';

import { StudentJobOfferDrawer } from './StudenJobOfferDrawer/StudentJobOfferDrawer';
import { StudentJobOfferCard } from './StudentJobOfferCard';

export function StudentJobOffers() {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const aclHelper = new ACLHelper(user);

  const offerTypeHandler = useQueryState('type', { defaultValue: '', shallow: false });
  const searchInputState = useQueryState('search', { defaultValue: '', shallow: false });
  const courseTypeHandler = useQueryState('course', { defaultValue: '', shallow: false });
  const [numberOfJoboffersShown, setNumberOfOffersShown] = useState(16);

  const { data: typeData } = useJobTypesQuery();
  const { data: courses } = useCoursesQuery();

  useContentScrollPosition();
  usePageHeading(t(translations.pages.studentJobOffers.title()));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [studentOffering, setStudentOffering] = useState<StudentJobOffer | undefined>(undefined);

  const {
    data: studentOffers,
    isLoading,
    isFetched,
    isError
  } = useStudentJobOfferingsQuery({
    params: {
      offset: 0,
      limit: numberOfJoboffersShown,
      search: searchInputState[0]?.length > 0 ? searchInputState[0] : undefined,
      jobType: offerTypeHandler[0],
      course: courseTypeHandler[0]?.length > 0 ? courseTypeHandler[0] : undefined
    }
  });

  const filterJobType = (): {
    label: string;
    name: number;
  }[] => {
    return typeData
      ? typeData?.map(jt => ({
          label: jt.name,
          name: jt.id
        }))
      : [];
  };

  const filterCourses = () => {
    return sortCourses(courses).map(c => ({
      label: `${c.name} (${c.shorthand})`,
      name: c.shorthand
    }));
  };

  const filters: Filter[] = [
    {
      field: 'search',
      label: t(translations.common.search()),
      stateHandler: searchInputState
    },
    {
      field: 'jobtype_id',
      label: t(translations.common.type()),
      select: {
        options: [{ label: '---', name: null }, ...filterJobType()],
        stateHandler: offerTypeHandler
      }
    },
    {
      field: 'course',
      label: t(translations.common.course()),
      select: {
        options: [{ label: '---', name: null }, ...filterCourses()],
        stateHandler: courseTypeHandler
      }
    }
  ];

  const handleClose = () => {
    setDrawerOpen(false);
    setStudentOffering(undefined);
  };

  const handleEdit = (offer: StudentJobOffer) => {
    setStudentOffering(offer);
    setDrawerOpen(true);
  };

  if (isLoading) {
    return (
      <Grid2 container spacing={2} marginTop={8}>
        {Array.from({ length: 8 }).map(() => (
          <Grid2 size={{ xs: 12, md: 12, lg: 6, xl: 4 }}>
            <Skeleton height={200} sx={{ transform: 'none', display: 'flex', flex: 1 }} />
          </Grid2>
        ))}
      </Grid2>
    );
  }

  if (isError || !studentOffers) {
    return;
  }

  return (
    <>
      {isShibUser(user) && (
        <StudentJobOfferDrawer
          open={drawerOpen}
          handleClose={handleClose}
          studentOffering={studentOffering}
        />
      )}
      <RichList
        filters={filters}
        controls={
          aclHelper.isStudent() ? <CreateButton onClick={() => setDrawerOpen(true)} /> : undefined
        }
      >
        {studentOffers.data.length === 0 && <NoItems />}
        <Grid2 container spacing={2}>
          {studentOffers.data.map(studentOffer => {
            return (
              <StudentJobOfferCard
                studentOffering={studentOffer}
                handleEdit={handleEdit}
                key={studentOffer.id}
              />
            );
          })}
        </Grid2>
        {isFetched && studentOffers.data.length === numberOfJoboffersShown && (
          <Waypoint
            bottomOffset={'-3%'}
            onEnter={() => {
              setNumberOfOffersShown(offset => offset + numberOfJoboffersShown);
            }}
          />
        )}
      </RichList>
    </>
  );
}
